<template>
	<div id="wrapper">
		<SidebarView :fixedAssets="true" :fixedAssetsVisible="true" :assets="true" />
		<div class="d-flex flex-column" id="content-wrapper">
			<div id="content">
				<TopbarView />
				<div class="mx-3">
					<div class="card card-shape home-box mb-3">
						<div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
							<h6 class="m-0 fw-bold text-green">
								Asset details <span class="text-muted">#{{ assetId }}</span>
							</h6>
							<div class="d-flex gap-2">
								<b-button variant="outline-secondary" class="btn-size" @click="$router.push('/assets')">
									Back
								</b-button>
								<b-button class="akkurate-green-btn btn-size text-white" @click="loadAll">
									<font-awesome-icon class="me-1" :icon="['fas', 'rotate']" />
									Refresh
								</b-button>
							</div>
						</div>
						<div class="card-body">
							<div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
							<div v-if="busy && !asset" class="text-muted small">
								<b-spinner small class="me-1" /> Loading...
							</div>

							<div v-if="asset" class="row g-2 mb-3 small">
								<div class="col-md-4"><strong>Name:</strong> {{ asset.asset_name }}</div>
								<div class="col-md-4"><strong>Status:</strong> {{ asset.status || "—" }}</div>
								<div class="col-md-4"><strong>Condition:</strong> {{ asset.condition_status || "—" }}</div>
								<div class="col-md-4"><strong>Branch:</strong> {{ asset.branch?.name || asset.branch_id || "—" }}</div>
								<div class="col-md-4"><strong>Category:</strong> {{ asset.category?.name || asset.category_id || "—" }}</div>
								<div class="col-md-4"><strong>Assigned to:</strong> {{ assignedUserLabel }}</div>
								<div class="col-md-4"><strong>Purchase date:</strong> {{ asset.purchase_date || "—" }}</div>
								<div class="col-md-4"><strong>Purchase cost:</strong> {{ asset.purchase_cost ?? "—" }}</div>
								<div class="col-md-4"><strong>Salvage value:</strong> {{ asset.salvage_value ?? "—" }}</div>
							</div>

							<div class="d-flex flex-wrap gap-2 mb-3">
								<b-button class="btn-size" variant="outline-secondary" @click="openAssign">Assign</b-button>
								<b-button class="btn-size" variant="outline-secondary" @click="openTransfer">Transfer</b-button>
								<b-button class="btn-size" variant="outline-secondary" @click="openMaintenance">Log maintenance</b-button>
								<b-button class="btn-size akkurate-green-btn text-white" @click="openDepreciate">Depreciate</b-button>
								<b-button class="btn-size" variant="danger" :disabled="!asset || asset.status === 'disposed'" @click="openDispose">Dispose</b-button>
							</div>

							<b-tabs content-class="mt-2" justified>
								<b-tab title="Movements">
									<b-table :busy="tabsBusy.movements" striped bordered responsive :items="movements" :fields="movementFields" show-empty class="table_text_size" />
								</b-tab>
								<b-tab title="Maintenance Logs">
									<b-table :busy="tabsBusy.maintenance" striped bordered responsive :items="maintenanceLogs" :fields="maintenanceFields" show-empty class="table_text_size" />
								</b-tab>
								<b-tab title="Depreciation Logs">
									<b-table :busy="tabsBusy.depreciation" striped bordered responsive :items="depreciationLogs" :fields="deprFields" show-empty class="table_text_size" />
								</b-tab>
							</b-tabs>
						</div>
					</div>
				</div>
			</div>
			<footer></footer>
		</div>
		<ChatBot />
		<a class="d-inline scroll-to-top" href="#page-top"><i class="fas fa-angle-up"></i></a>

		<!-- Assign -->
		<b-modal v-model="modals.assign" title="Assign asset" @hidden="resetAssign">
			<div class="small">
				<label class="akkurate-dark small">Assign to</label>
				<multiselect v-model="assignForm.user" :options="users" label="full_name" track-by="id" placeholder="Unassigned" class="field-container" />
				<label class="akkurate-dark small mt-2">Reason</label>
				<b-form-textarea v-model="assignForm.reason" rows="2" class="field-container" />
				<div v-if="assignError" class="alert alert-danger py-2 small mt-2 mb-0">{{ assignError }}</div>
			</div>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button variant="secondary" class="me-2" @click="modals.assign = false">Cancel</b-button>
					<b-button class="akkurate-green-btn text-white" :disabled="actionBusy" @click="submitAssign">Save</b-button>
				</div>
			</template>
		</b-modal>

		<!-- Transfer -->
		<b-modal v-model="modals.transfer" title="Transfer asset" @hidden="resetTransfer">
			<div class="small">
				<label class="akkurate-dark small">To branch <span class="text-danger">*</span></label>
				<multiselect v-model="transferForm.branch" :options="branches" label="name" track-by="id" class="field-container" />
				<label class="akkurate-dark small mt-2">To user (optional)</label>
				<multiselect v-model="transferForm.user" :options="users" label="full_name" track-by="id" placeholder="Unassigned" class="field-container" />
				<label class="akkurate-dark small mt-2">Reason</label>
				<b-form-textarea v-model="transferForm.reason" rows="2" class="field-container" />
				<div v-if="transferError" class="alert alert-danger py-2 small mt-2 mb-0">{{ transferError }}</div>
			</div>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button variant="secondary" class="me-2" @click="modals.transfer = false">Cancel</b-button>
					<b-button class="akkurate-green-btn text-white" :disabled="actionBusy" @click="submitTransfer">Transfer</b-button>
				</div>
			</template>
		</b-modal>

		<!-- Maintenance -->
		<b-modal v-model="modals.maintenance" title="Maintenance log" @hidden="resetMaintenance">
			<div class="row g-2 small">
				<div class="col-md-6">
					<label class="akkurate-dark small">Maintenance date <span class="text-danger">*</span></label>
					<b-form-input v-model="maintForm.maintenance_date" type="date" class="field-container" />
				</div>
				<div class="col-md-6">
					<label class="akkurate-dark small">Type <span class="text-danger">*</span></label>
					<b-form-select v-model="maintForm.maintenance_type" :options="maintTypeOptions" class="field-container" />
				</div>
				<div class="col-md-6">
					<label class="akkurate-dark small">Vendor</label>
					<b-form-input v-model="maintForm.vendor" class="field-container" />
				</div>
				<div class="col-md-6">
					<label class="akkurate-dark small">Cost</label>
					<b-form-input v-model.number="maintForm.cost" type="number" step="any" min="0" class="field-container" />
				</div>
				<div class="col-12">
					<label class="akkurate-dark small">Notes</label>
					<b-form-textarea v-model="maintForm.notes" rows="2" class="field-container" />
				</div>
				<div v-if="maintError" class="col-12">
					<div class="alert alert-danger py-2 small mb-0">{{ maintError }}</div>
				</div>
			</div>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button variant="secondary" class="me-2" @click="modals.maintenance = false">Cancel</b-button>
					<b-button class="akkurate-green-btn text-white" :disabled="actionBusy" @click="submitMaintenance">Save</b-button>
				</div>
			</template>
		</b-modal>

		<!-- Depreciate -->
		<b-modal v-model="modals.depreciate" title="Depreciate asset" @hidden="resetDepreciate">
			<div class="row g-2 small">
				<div class="col-md-6">
					<label class="akkurate-dark small">Period start <span class="text-danger">*</span></label>
					<b-form-input v-model="deprForm.period_start" type="date" class="field-container" />
				</div>
				<div class="col-md-6">
					<label class="akkurate-dark small">Period end <span class="text-danger">*</span></label>
					<b-form-input v-model="deprForm.period_end" type="date" class="field-container" />
				</div>
				<div v-if="deprError" class="col-12">
					<div class="alert alert-danger py-2 small mb-0">{{ deprError }}</div>
				</div>
				<div v-if="deprResult" class="col-12">
					<div class="alert alert-success py-2 small mb-0">{{ deprResult }}</div>
				</div>
			</div>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button variant="secondary" class="me-2" @click="modals.depreciate = false">Close</b-button>
					<b-button class="akkurate-green-btn text-white" :disabled="actionBusy" @click="submitDepreciate">Run</b-button>
				</div>
			</template>
		</b-modal>

		<!-- Dispose -->
		<b-modal v-model="modals.dispose" title="Dispose asset" @hidden="resetDispose" size="lg">
			<div class="row g-2 small">
				<div class="col-md-6">
					<label class="akkurate-dark small">Disposal date <span class="text-danger">*</span></label>
					<b-form-input v-model="disposeForm.disposal_date" type="date" class="field-container" />
				</div>
				<div class="col-md-6">
					<label class="akkurate-dark small">Disposal value</label>
					<b-form-input v-model.number="disposeForm.disposal_value" type="number" step="any" min="0" class="field-container" />
				</div>
				<div class="col-md-6">
					<label class="akkurate-dark small">Proceeds COA</label>
					<multiselect v-model="disposeForm.proceeds_coa" :options="coaSubheads" label="name" track-by="id" class="field-container" />
				</div>
				<div class="col-md-6">
					<label class="akkurate-dark small">Gain/Loss COA</label>
					<multiselect v-model="disposeForm.gain_loss_coa" :options="coaSubheads" label="name" track-by="id" class="field-container" />
				</div>
				<div class="col-12">
					<label class="akkurate-dark small">Reason</label>
					<b-form-input v-model="disposeForm.reason" class="field-container" />
				</div>
				<div class="col-12">
					<label class="akkurate-dark small">Notes</label>
					<b-form-textarea v-model="disposeForm.notes" rows="2" class="field-container" />
				</div>
				<div v-if="disposeError" class="col-12">
					<div class="alert alert-danger py-2 small mb-0">{{ disposeError }}</div>
				</div>
			</div>
			<template #modal-footer>
				<div class="w-100 d-flex justify-content-end">
					<b-button variant="secondary" class="me-2" @click="modals.dispose = false">Cancel</b-button>
					<b-button variant="danger" class="btn-size text-white" :disabled="actionBusy" @click="submitDispose">Dispose</b-button>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import SidebarView from "@/views/main/components/Sidebar.vue";
import TopbarView from "@/views/main/components/Topbar.vue";
import ChatBot from "@/views/components/AI/ChatBot.vue";
import ApiService from "@/core/services/api.service";
import Multiselect from "vue-multiselect";

export default {
	name: "AssetView",
	components: { SidebarView, TopbarView, ChatBot, Multiselect },
	props: ["id"],
	data() {
		return {
			busy: false,
			error: "",
			asset: null,
			movements: [],
			maintenanceLogs: [],
			depreciationLogs: [],
			tabsBusy: { movements: false, maintenance: false, depreciation: false },
			movementFields: [
				{ key: "id", label: "#" },
				{ key: "type", label: "Type" },
				{ key: "from_branch", label: "From" },
				{ key: "to_branch", label: "To" },
				{ key: "reason", label: "Reason" },
				{ key: "created_at", label: "Created" },
			],
			maintenanceFields: [
				{ key: "maintenance_date", label: "Date" },
				{ key: "maintenance_type", label: "Type" },
				{ key: "vendor", label: "Vendor" },
				{ key: "cost", label: "Cost" },
				{ key: "notes", label: "Notes" },
			],
			deprFields: [
				{ key: "period_start", label: "Start" },
				{ key: "period_end", label: "End" },
				{ key: "amount", label: "Amount" },
				{ key: "created_at", label: "Created" },
			],

			users: [],
			branches: [],
			coaSubheads: [],

			modals: { assign: false, transfer: false, maintenance: false, depreciate: false, dispose: false },
			actionBusy: false,

			assignForm: { user: null, reason: "" },
			assignError: "",

			transferForm: { branch: null, user: null, reason: "" },
			transferError: "",

			maintForm: { maintenance_date: "", maintenance_type: "service", vendor: "", cost: null, notes: "" },
			maintError: "",
			maintTypeOptions: [
				{ value: "repair", text: "Repair" },
				{ value: "service", text: "Service" },
				{ value: "inspection", text: "Inspection" },
				{ value: "other", text: "Other" },
			],

			deprForm: { period_start: "", period_end: "" },
			deprError: "",
			deprResult: "",

			disposeForm: {
				disposal_date: "",
				disposal_value: null,
				reason: "",
				notes: "",
				proceeds_coa: null,
				gain_loss_coa: null,
			},
			disposeError: "",
		};
	},
	computed: {
		assetId() {
			return this.id || this.$route.params.id;
		},
		assignedUserLabel() {
			if (!this.asset) return "—";
			const u =
				this.asset.assigned_user ||
				this.users.find((x) => x.id === this.asset.assigned_to) ||
				null;
			return u ? (u.full_name || u.username || `User #${u.id}`) : "Unassigned";
		},
	},
	created() {
		this.loadDropdowns();
		this.loadAll();
	},
	methods: {
		async loadDropdowns() {
			try {
				const [us, br, jr] = await Promise.all([
					ApiService.get("/users/dropdown").catch(() => ({ data: {} })),
					ApiService.get("/settings/branches/dropdown").catch(() => ({ data: {} })),
					ApiService.get("/journals/dropdown").catch(() => ({ data: {} })),
				]);
				this.users = us.data?.data || us.data || [];
				this.branches = br.data?.data || br.data?.branches || br.data || [];
				this.coaSubheads = jr.data?.coa_subheads || [];
			} catch (e) {
				/* non-blocking */
			}
		},
		async loadAll() {
			await Promise.all([this.loadAsset(), this.loadMovements(), this.loadMaintenanceLogs(), this.loadDepreciationLogs()]);
		},
		async loadAsset() {
			this.busy = true;
			this.error = "";
			try {
				const res = await ApiService.get(`/assets/${this.assetId}`);
				this.asset = res.data?.data || res.data || null;
			} catch (e) {
				this.asset = null;
				this.error = e.response?.data?.message || "Could not load asset.";
			} finally {
				this.busy = false;
			}
		},
		async loadMovements() {
			this.tabsBusy.movements = true;
			try {
				const res = await ApiService.get(`/assets/${this.assetId}/movements`);
				this.movements = res.data?.data || res.data || [];
			} catch (e) {
				this.movements = [];
			} finally {
				this.tabsBusy.movements = false;
			}
		},
		async loadMaintenanceLogs() {
			this.tabsBusy.maintenance = true;
			try {
				const res = await ApiService.get(`/assets/${this.assetId}/maintenance-logs`);
				this.maintenanceLogs = res.data?.data || res.data || [];
			} catch (e) {
				this.maintenanceLogs = [];
			} finally {
				this.tabsBusy.maintenance = false;
			}
		},
		async loadDepreciationLogs() {
			this.tabsBusy.depreciation = true;
			try {
				const res = await ApiService.get(`/assets/${this.assetId}/depreciation-logs`);
				this.depreciationLogs = res.data?.data || res.data || [];
			} catch (e) {
				this.depreciationLogs = [];
			} finally {
				this.tabsBusy.depreciation = false;
			}
		},

		openAssign() {
			this.modals.assign = true;
		},
		resetAssign() {
			this.assignForm = { user: null, reason: "" };
			this.assignError = "";
		},
		async submitAssign() {
			this.actionBusy = true;
			this.assignError = "";
			try {
				await ApiService.post(`/assets/${this.assetId}/assign`, {
					assigned_to: this.assignForm.user?.id || null,
					reason: this.assignForm.reason || undefined,
				});
				toast.fire({ icon: "success", title: "Assignment updated" });
				this.modals.assign = false;
				this.loadAll();
			} catch (e) {
				this.assignError = e.response?.data?.message || "Assign failed.";
			} finally {
				this.actionBusy = false;
			}
		},

		openTransfer() {
			this.modals.transfer = true;
		},
		resetTransfer() {
			this.transferForm = { branch: null, user: null, reason: "" };
			this.transferError = "";
		},
		async submitTransfer() {
			if (!this.transferForm.branch?.id) {
				this.transferError = "Please select a destination branch.";
				return;
			}
			this.actionBusy = true;
			this.transferError = "";
			try {
				await ApiService.post(`/assets/${this.assetId}/transfer`, {
					to_branch_id: this.transferForm.branch.id,
					to_user_id: this.transferForm.user?.id || null,
					reason: this.transferForm.reason || undefined,
				});
				toast.fire({ icon: "success", title: "Transfer recorded" });
				this.modals.transfer = false;
				this.loadAll();
			} catch (e) {
				this.transferError = e.response?.data?.message || "Transfer failed.";
			} finally {
				this.actionBusy = false;
			}
		},

		openMaintenance() {
			this.modals.maintenance = true;
		},
		resetMaintenance() {
			this.maintForm = { maintenance_date: "", maintenance_type: "service", vendor: "", cost: null, notes: "" };
			this.maintError = "";
		},
		async submitMaintenance() {
			this.actionBusy = true;
			this.maintError = "";
			try {
				await ApiService.post(`/assets/${this.assetId}/maintenance`, {
					maintenance_date: this.maintForm.maintenance_date,
					maintenance_type: this.maintForm.maintenance_type,
					vendor: this.maintForm.vendor || undefined,
					cost: this.maintForm.cost === null || this.maintForm.cost === "" ? undefined : Number(this.maintForm.cost),
					notes: this.maintForm.notes || undefined,
				});
				toast.fire({ icon: "success", title: "Maintenance logged" });
				this.modals.maintenance = false;
				this.loadAll();
			} catch (e) {
				if (e.response?.status === 422 && e.response?.data?.message) {
					this.maintError = e.response.data.message;
				} else {
					this.maintError = "Maintenance save failed.";
				}
			} finally {
				this.actionBusy = false;
			}
		},

		openDepreciate() {
			this.modals.depreciate = true;
		},
		resetDepreciate() {
			this.deprForm = { period_start: "", period_end: "" };
			this.deprError = "";
			this.deprResult = "";
		},
		async submitDepreciate() {
			this.actionBusy = true;
			this.deprError = "";
			this.deprResult = "";
			try {
				const res = await ApiService.post(`/assets/${this.assetId}/depreciate`, {
					period_start: this.deprForm.period_start,
					period_end: this.deprForm.period_end,
				});
				this.deprResult = res.data?.message || "Depreciation completed.";
				toast.fire({ icon: "success", title: this.deprResult });
				await this.loadAll();
			} catch (e) {
				this.deprError = e.response?.data?.message || "Depreciation failed.";
			} finally {
				this.actionBusy = false;
			}
		},

		openDispose() {
			this.modals.dispose = true;
		},
		resetDispose() {
			this.disposeForm = { disposal_date: "", disposal_value: null, reason: "", notes: "", proceeds_coa: null, gain_loss_coa: null };
			this.disposeError = "";
		},
		async submitDispose() {
			if (!this.disposeForm.disposal_date) {
				this.disposeError = "Disposal date is required.";
				return;
			}
			this.actionBusy = true;
			this.disposeError = "";
			try {
				await ApiService.post(`/assets/${this.assetId}/dispose`, {
					disposal_date: this.disposeForm.disposal_date,
					disposal_value: this.disposeForm.disposal_value == null || this.disposeForm.disposal_value === "" ? undefined : Number(this.disposeForm.disposal_value),
					reason: this.disposeForm.reason || undefined,
					notes: this.disposeForm.notes || undefined,
					disposal_proceeds_coa_subhead_id: this.disposeForm.proceeds_coa?.id || undefined,
					disposal_gain_loss_coa_subhead_id: this.disposeForm.gain_loss_coa?.id || undefined,
				});
				toast.fire({ icon: "success", title: "Asset disposed" });
				this.modals.dispose = false;
				this.loadAll();
			} catch (e) {
				this.disposeError = e.response?.data?.message || "Dispose failed.";
			} finally {
				this.actionBusy = false;
			}
		},
	},
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

